/**
 * InMocean Order Data Service
 * Handles order file uploads to Google Cloud Storage and calls backend Cloud Functions.
 * Follows the exact same pattern as InMoceanDataService but for order files.
 */

import { getStorage, ref, uploadBytes, getDownloadURL } from 'firebase/storage';
import { getFunctions, httpsCallable } from 'firebase/functions';
import { getFirestore, collection, query, orderBy, limit, onSnapshot } from 'firebase/firestore'; 
import { ORDER_FILE_TYPES, INMOCEAN_ORDER_SCHEMAS } from '../config/orderFileSchemas';

class InMoceanOrderDataServiceClass {
    constructor() {
        this.storage = null;
        this.functions = null;
        this.db = null;
        this.bucketPrefix = 'inmocean-orders-raw'; // Different bucket from costing files

        console.log('🔧 InMoceanOrderDataService initialized');
    }

    /**
     * Initialize Firebase service connections
     */
    async initialize() {
        try {
            if (!this.storage) {
                this.storage = getStorage();
                console.log('✅ InMocean order storage initialized');
            }
            if (!this.functions) {
                this.functions = getFunctions();
                console.log('✅ Firebase Functions initialized for orders');
            }
             if (!this.db) {
                this.db = getFirestore();
                console.log('✅ Firestore initialized for order real-time updates');
            }
        } catch (error) {
            console.error('❌ Failed to initialize Firebase services for orders:', error);
            throw error;
        }
    }

    /**
     * Uploads an order file to Cloud Storage, which triggers the backend processing function.
     * EXACT SAME LOGIC as InMoceanDataService.uploadAndProcessFile but for order files
     */
    async uploadAndProcessFile(file, fileType, preValidatedData = null) {
        try {
            console.log(`🚀 Starting order upload for ${fileType}:`, file.name);
            await this.initialize();

            // The ONLY job of the frontend is to upload the file.
            // The backend function will handle the rest and route to inmocean_orders dataset
            const storageUrl = await this.uploadToStorage(file, fileType);
            
            const result = {
                fileName: file.name,
                rowCount: preValidatedData ? preValidatedData.length : 0, // Use pre-validated count for UI
                uploadTime: new Date().toISOString(),
                fileSize: file.size,
                storageUrl: storageUrl,
                status: 'processing' // The backend is now processing the file
            };
            
            console.log(`✅ Order file uploaded for processing for ${fileType}:`, result);
            return result;

        } catch (error) {
            console.error(`❌ Order upload failed for ${fileType}:`, error);
            throw error;
        }
    }

    /**
     * Upload order file to Google Cloud Storage
     * EXACT SAME LOGIC as InMoceanDataService.uploadToStorage but with order bucket
     */
    async uploadToStorage(file, fileType) {
        try {
            if (!this.storage) {
                await this.initialize();
            }

            const timestamp = new Date().toISOString().replace(/[:.]/g, '-');
            const filename = `${fileType}/${timestamp}_${file.name}`;
            const storageRef = ref(this.storage, `${this.bucketPrefix}/${filename}`);

            console.log(`📤 Uploading order file to storage: ${filename}`);
            const snapshot = await uploadBytes(storageRef, file);
            const downloadURL = await getDownloadURL(snapshot.ref);

            console.log(`✅ Order file uploaded to storage: ${downloadURL}`);
            return downloadURL;

        } catch (error) {
            console.error('❌ Order storage upload failed:', error);
            throw new Error(`Order storage upload failed: ${error.message}`);
        }
    }
    
    /**
     * Listens for real-time updates from the `order_processing_jobs` collection in Firestore.
     * EXACT SAME LOGIC as InMoceanDataService.listenToUploadHistory but for order files
     */
    listenToOrderUploadHistory(callback) {
        this.initialize();
        // Use separate collection for order processing jobs
        const historyCollectionRef = collection(this.db, 'order_processing_jobs');
        
        const q = query(historyCollectionRef, orderBy('startTime', 'desc'), limit(50));

        const unsubscribe = onSnapshot(q, (querySnapshot) => {
            const history = {};
            // Initialize all order file types so the UI doesn't flicker
            ORDER_FILE_TYPES.forEach(ft => {
                history[ft] = { status: 'pending', rowCount: 0, lastUpload: null };
            });

            querySnapshot.forEach((doc) => {
                const job = doc.data();
                // If we haven't recorded a more recent job for this file type, update it.
                if (!history[job.fileType] || (job.startTime.toDate() > (history[job.fileType].lastUpload ? new Date(history[job.fileType].lastUpload) : 0))) {
                    history[job.fileType] = {
                        lastUpload: job.startTime.toDate().toISOString(),
                        rowCount: job.details?.rowsInserted || 0,
                        status: job.status,
                        fileName: job.fileName,
                        error: job.status === 'error' ? job.details.error : null
                    };
                }
            });
            console.log('🔄 Order upload history updated from Firestore listener.');
            callback(history);
        }, (error) => {
            console.error("❌ Error listening to order upload history:", error);
        });

        return unsubscribe; // Return the unsubscribe function to be called on cleanup
    }

    /**
     * Query order data by calling the real `getBigQueryData` Cloud Function.
     * EXACT SAME LOGIC as InMoceanDataService.queryTableData but for order files
     */
    async queryTableData(fileType, limit = 100) {
        try {
            await this.initialize();
            
            console.log(`🔍 Calling "getBigQueryData" function for order file type: ${fileType}`);
            
            const getBigQueryData = httpsCallable(this.functions, 'getBigQueryData');
            const result = await getBigQueryData({ 
                fileType: fileType, 
                limit: limit,
                datasetType: 'orders' // Tell backend this is order data
            });

            const data = result.data; // The returned data is on the .data property
            
            console.log(`✅ Order real-time query completed: ${data.length} rows returned`);
            return data;
            
        } catch (error) {
            console.error('❌ BigQuery order query via Cloud Function failed:', error);
            // Re-throw the error so the UI can display it
            throw new Error(`Failed to query ${fileType} order data: ${error.message}`);
        }
    }
}

// Export singleton instance
export const InMoceanOrderDataService = new InMoceanOrderDataServiceClass();